/**
 * VD — КБЖУ (числовой подбор, основы, сравнение) и личный подбор (BFF /api/v1/personal-selection).
 *
 * Чистые функции + тонкий клиент. Поля ответа BFF — как в движке (camelCase), без переименований:
 * GET → {status, mode, includeTraces, generation, revision, sessionGeneration, factsRevision}.
 * Список ограничений покупателя сюда НЕ приходит (это PII профиля) — подписи берёт страница из
 * /v1/profile/constraints и передаёт в компонент. Ошибки BFF: {error, message, details:{status,...}}.
 */
import { StorefrontApiError } from "./storefront";
import type { RecipeNutrition, RecipeNutritionFilters } from "./types";

// ─── Нутриенты и границы ─────────────────────────────────────────────────

/** Имена совпадают с vd-culinary-extension (NUTRIENTS) и параметрами `<n>_min`/`<n>_max`. */
export const NUTRIENTS = ["kcal", "protein_g", "fat_g", "carbs_g"] as const;
export type Nutrient = (typeof NUTRIENTS)[number];
export type NutrientValues = Record<Nutrient, number | null>;
export interface NumericRange { min?: number; max?: number }
export type NutrientConstraints = Partial<Record<Nutrient, NumericRange>>;
export interface RangeDraft { min: string; max: string }
export type RangeDrafts = Record<Nutrient, RangeDraft>;
export type RangeErrorCode = "not_number" | "negative" | "too_large" | "min_gt_max";
export interface RangeError { nutrient: Nutrient; bound: "min" | "max"; code: RangeErrorCode }

/** Как MAX_CUSTOM_AMOUNT в vd-culinary-extension: защитная верхняя граница ввода. */
export const MAX_INPUT_VALUE = 100_000;

export const NUTRIENT_LABEL: Record<Nutrient, string> = { kcal: "Ккал", protein_g: "Белки", fat_g: "Жиры", carbs_g: "Углеводы" };
export const NUTRIENT_UNIT: Record<Nutrient, string> = { kcal: "ккал", protein_g: "г", fat_g: "г", carbs_g: "г" };

export function emptyRanges(): RangeDrafts {
  return { kcal: { min: "", max: "" }, protein_g: { min: "", max: "" }, fat_g: { min: "", max: "" }, carbs_g: { min: "", max: "" } };
}

const DECIMAL = /^\d+(?:[.,]\d+)?$|^-\d+(?:[.,]\d+)?$/;

function parseBound(raw: string): { ok: true; value: number | undefined } | { ok: false; code: RangeErrorCode } {
  const t = raw.trim();
  if (t === "") return { ok: true, value: undefined };
  if (!DECIMAL.test(t)) return { ok: false, code: "not_number" };
  const v = Number(t.replace(",", "."));
  if (!Number.isFinite(v)) return { ok: false, code: "not_number" };
  if (v < 0) return { ok: false, code: "negative" };
  if (v > MAX_INPUT_VALUE) return { ok: false, code: "too_large" };
  return { ok: true, value: v };
}

/** Пустое поле = нет границы (не 0). Границы включительные. */
export function parseRanges(drafts: RangeDrafts):
  | { ok: true; constraints: NutrientConstraints }
  | { ok: false; errors: RangeError[] } {
  const constraints: NutrientConstraints = {};
  const errors: RangeError[] = [];
  for (const n of NUTRIENTS) {
    const d = drafts[n] ?? { min: "", max: "" };
    const min = parseBound(d.min), max = parseBound(d.max);
    if (!min.ok) errors.push({ nutrient: n, bound: "min", code: min.code });
    if (!max.ok) errors.push({ nutrient: n, bound: "max", code: max.code });
    if (!min.ok || !max.ok) continue;
    if (min.value !== undefined && max.value !== undefined && min.value > max.value) {
      errors.push({ nutrient: n, bound: "max", code: "min_gt_max" });
      continue;
    }
    const r: NumericRange = {};
    if (min.value !== undefined) r.min = min.value;
    if (max.value !== undefined) r.max = max.value;
    if (r.min !== undefined || r.max !== undefined) constraints[n] = r;
  }
  return errors.length ? { ok: false, errors } : { ok: true, constraints };
}

export function rangesToDrafts(c: NutrientConstraints | undefined): RangeDrafts {
  const d = emptyRanges();
  for (const n of NUTRIENTS) {
    const r = c?.[n];
    if (r?.min !== undefined) d[n].min = String(r.min);
    if (r?.max !== undefined) d[n].max = String(r.max);
  }
  return d;
}

export function activeConstraintCount(c: NutrientConstraints): number {
  return NUTRIENTS.filter((n) => c[n]?.min !== undefined || c[n]?.max !== undefined).length;
}

export function rangeErrorText(e: RangeError): string {
  const name = NUTRIENT_LABEL[e.nutrient];
  switch (e.code) {
    case "not_number": return `${name}: введите число`;
    case "negative": return `${name}: число не может быть отрицательным`;
    case "too_large": return `${name}: слишком большое число`;
    case "min_gt_max": return `${name}: «от» больше, чем «до»`;
  }
}

// ─── Основа КБЖУ ─────────────────────────────────────────────────────────

export type NutritionBasis =
  | { kind: "per100g" }
  | { kind: "per100ml" }
  | { kind: "pack" }
  | { kind: "custom"; amount: number; unit: "g" | "ml" }
  | { kind: "original_serving" };

export function validateBasis(b: NutritionBasis): null | "custom_amount" {
  if (b.kind !== "custom") return null;
  return Number.isFinite(b.amount) && b.amount > 0 && b.amount <= MAX_INPUT_VALUE ? null : "custom_amount";
}

const fmtAmount = (v: number) => new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(v);

export function basisLabel(b: NutritionBasis): string {
  switch (b.kind) {
    case "per100g": return "на 100 г";
    case "per100ml": return "на 100 мл";
    case "pack": return "на упаковку";
    case "custom": return `на ${fmtAmount(b.amount)} ${b.unit === "ml" ? "мл" : "г"}`;
    case "original_serving": return "на порцию из рецепта";
  }
}

/** Ключ сравнимости: разные ключи нельзя ставить в одну таблицу или один числовой отбор. */
export function basisKey(b: NutritionBasis): string {
  return b.kind === "custom" ? `custom:${b.amount}:${b.unit}` : b.kind;
}

/** Параметры запроса подбора: `basis` + `<nutrient>_min/_max` (общие имена с BFF подбора). */
export function nutritionQueryParams(b: NutritionBasis, c: NutrientConstraints): URLSearchParams {
  const p = new URLSearchParams();
  p.set("basis", b.kind);
  if (b.kind === "custom") { p.set("amount", String(b.amount)); p.set("unit", b.unit); }
  for (const n of NUTRIENTS) {
    const r = c[n];
    if (r?.min !== undefined) p.set(`${n}_min`, String(r.min));
    if (r?.max !== undefined) p.set(`${n}_max`, String(r.max));
  }
  return p;
}

/** Неизвестное — прочерк, никогда не 0. */
export function formatNutrient(n: Nutrient, v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return new Intl.NumberFormat("ru-RU", { maximumFractionDigits: n === "kcal" ? 0 : 1 }).format(v);
}

// ─── Личный подбор: контракт BFF ─────────────────────────────────────────

export type PersonalSelectionMode = "hide" | "warn" | "ignore";
export const PERSONAL_SELECTION_MODES: readonly PersonalSelectionMode[] = ["hide", "warn", "ignore"];

export interface PersonalSelectionSnapshot {
  status: "ok";
  mode: PersonalSelectionMode;
  includeTraces: boolean;
  generation: number;
  revision: number;
  sessionGeneration: string;
  /** Есть в GET; в ответах записи (mode/reset/confirm) отсутствует. */
  factsRevision?: string;
}
export type PersonalSelectionWriteOk = Omit<PersonalSelectionSnapshot, "factsRevision">;

export interface SelectionReason { kind: "contains" | "traces" | "unknown"; value: string; source: "personal" | "explicit" }
export interface SelectionDecision {
  decision: "allow" | "block" | "unknown_metadata" | "confirmation_required";
  visible: boolean;
  compatibilityKnown?: boolean;
  reasons: SelectionReason[];
  confirmationKey: string | null;
}
export interface SelectionItemDecision {
  itemId: string;
  status: string;
  decision?: SelectionDecision;
  mode?: PersonalSelectionMode;
  generation?: number;
  revision?: number;
  sessionGeneration?: string;
}

export interface PersonalSelectionApi {
  get(): Promise<PersonalSelectionSnapshot>;
  setMode(body: { expectedRevision: number; mode: PersonalSelectionMode; includeTraces?: boolean }): Promise<PersonalSelectionWriteOk>;
  reset(): Promise<PersonalSelectionWriteOk>;
  evaluate(itemIds: string[]): Promise<{ decisions: SelectionItemDecision[] }>;
  confirm(body: { itemId: string; expectedGeneration: number; expectedRevision: number; warningKey: string }): Promise<PersonalSelectionWriteOk>;
}

/** Движок: evaluate принимает до 100 id за раз. */
export const EVALUATE_BATCH = 100;

function defaultBase(): string {
  if (typeof window !== "undefined") {
    const inj = (window as unknown as { __API_BASE__?: string }).__API_BASE__;
    if (inj) return inj;
  }
  const env = typeof import.meta !== "undefined" ? import.meta.env?.VITE_STOREFRONT_API : undefined;
  if (!env) return "/api";
  const t = String(env).replace(/\/$/, "");
  return t.endsWith("/api") ? t : `${t}/api`;
}

// Only this module can create an opt-out proof after validating a successful server config response.
class PersonalSelectionDisabled extends Error {}

export function createPersonalSelectionApi(opts: { base?: string; fetchImpl?: typeof fetch } = {}): PersonalSelectionApi {
  const base = opts.base ?? defaultBase();
  const doFetch = (url: string, init: RequestInit) =>
    (opts.fetchImpl ?? globalThis.fetch.bind(globalThis))(url, init);

  async function call<T>(method: "GET" | "POST", action: string, body?: unknown): Promise<T> {
    const res = await doFetch(`${base}/v1/personal-selection${action ? "/" + action : ""}`, {
      method,
      credentials: "include",
      headers: { Accept: "application/json", ...(body !== undefined ? { "Content-Type": "application/json" } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const text = await res.text();
    let payload: unknown = null;
    if (text) { try { payload = JSON.parse(text); } catch { payload = text; } }
    if (!res.ok) {
      const m = payload && typeof payload === "object" ? (payload as { message?: unknown }).message : undefined;
      throw new StorefrontApiError(typeof m === "string" && m.trim() ? m : `HTTP ${res.status}`, res.status, payload);
    }
    return payload as T;
  }

  async function requireEnabled(): Promise<void> {
    const config = await call<{ enabled?: unknown }>("GET", "config");
    if (config?.enabled === false) throw new PersonalSelectionDisabled("Личный подбор выключен сервером");
    if (config?.enabled !== true) throw new Error("Некорректный ответ конфигурации подбора");
  }
  async function enabledCall<T>(method: "GET" | "POST", action: string, body?: unknown): Promise<T> {
    await requireEnabled();
    return call<T>(method, action, body);
  }

  return {
    get: () => enabledCall("GET", ""),
    setMode: (body) => enabledCall("POST", "mode", body),
    reset: () => enabledCall("POST", "reset", {}),
    confirm: (body) => enabledCall("POST", "confirm", body),
    async evaluate(itemIds) {
      await requireEnabled();
      const ids = [...new Set(itemIds)];
      const decisions: SelectionItemDecision[] = [];
      for (let i = 0; i < ids.length; i += EVALUATE_BATCH) {
        const r = await call<{ decisions?: SelectionItemDecision[] }>("POST", "evaluate", { itemIds: ids.slice(i, i + EVALUATE_BATCH) });
        decisions.push(...(Array.isArray(r?.decisions) ? r.decisions : []));
      }
      return { decisions };
    },
  };
}

export const personalSelectionApi: PersonalSelectionApi = createPersonalSelectionApi();

export type SelectionErrorCode = "conflict" | "stale" | "not_pending" | "invalid" | "not_found" | "unauthorized" | "unavailable" | "feature_off";

/** Runtime server config decides opt-out. A missing build flag must never bypass the checks. */
export function personalSelectionFeatureEnabled(): boolean {
  return true;
}

/** Код ошибки из ответа BFF (вложенный details.status движка) или из HTTP-статуса. */
export function selectionErrorCode(err: unknown): SelectionErrorCode {
  if (err instanceof PersonalSelectionDisabled) return "feature_off";
  if (!(err instanceof StorefrontApiError)) return "unavailable";
  if (err.statusCode >= 500) return "unavailable";
  const p = err.payload && typeof err.payload === "object" ? err.payload as { details?: unknown; status?: unknown; error?: unknown } : null;
  const nested = p?.details && typeof p.details === "object" ? (p.details as { status?: unknown }).status : p?.status;
  if (nested === "conflict" || nested === "stale" || nested === "not_pending") return nested;
  if (nested === "invalid_input" || nested === "invalid_scope") return "invalid";
  switch (err.statusCode) {
    case 401: return "unauthorized";
    case 404: return "not_found";
    case 400: return "invalid";
    case 409: return "conflict";
    default: return "unavailable";
  }
}

// ─── Адаптеры к контракту рецептов основной ветки (RecipeNutritionFilters / RecipeNutrition) ──


const RECIPE_FILTER_KEY: Record<Nutrient, { min: keyof RecipeNutritionFilters; max: keyof RecipeNutritionFilters }> = {
  kcal: { min: "kcalMin", max: "kcalMax" },
  protein_g: { min: "proteinMin", max: "proteinMax" },
  fat_g: { min: "fatMin", max: "fatMax" },
  carbs_g: { min: "carbsMin", max: "carbsMax" },
};

/** Границы фильтра → параметры `storefront.storefrontContent.recipes()`; пустые границы не передаются. */
export function toRecipeNutritionFilters(c: NutrientConstraints): RecipeNutritionFilters {
  const out: Record<string, number> = {};
  for (const n of NUTRIENTS) {
    const r = c[n];
    if (r?.min !== undefined) out[RECIPE_FILTER_KEY[n].min] = r.min;
    if (r?.max !== undefined) out[RECIPE_FILTER_KEY[n].max] = r.max;
  }
  return out as RecipeNutritionFilters;
}

export interface NutritionView {
  basis: NutritionBasis;
  status: "complete" | "partial" | "unavailable";
  values: NutrientValues;
  reason: string | null;
  assumptions: string[];
}

const NULL_VALUES = (): NutrientValues => ({ kcal: null, protein_g: null, fat_g: null, carbs_g: null });
const RECIPE_UNAVAILABLE_REASON: Record<string, string> = {
  unavailable: "КБЖУ порции по этому рецепту не оценено.",
  stale: "Рецепт или данные о продуктах изменились — КБЖУ пересчитывается.",
  unverifiable: "Не удалось проверить актуальность данных — КБЖУ не показываем.",
  no_data: "Данных о КБЖУ для этого рецепта нет.",
};

/** Ответ рецепта → пропсы NutritionValues. Неактуальные/непроверяемые данные не показываются числами. */
export function recipeNutritionView(n: RecipeNutrition | undefined | null): NutritionView {
  const basis: NutritionBasis = { kind: "original_serving" };
  if (!n) return { basis, status: "unavailable", values: NULL_VALUES(), reason: RECIPE_UNAVAILABLE_REASON.no_data, assumptions: [] };
  if (n.status === "available" || n.status === "partial") {
    const values = NULL_VALUES();
    for (const k of NUTRIENTS) {
      const v = n.perServing?.[k];
      values[k] = typeof v === "number" && Number.isFinite(v) ? v : null;
    }
    const partial = n.status === "partial" || NUTRIENTS.some((k) => values[k] === null);
    return { basis, status: partial ? "partial" : "complete", values, reason: n.reason ?? null, assumptions: [...(n.assumptions ?? [])] };
  }
  return { basis, status: "unavailable", values: NULL_VALUES(), reason: n.reason || RECIPE_UNAVAILABLE_REASON[n.status] || RECIPE_UNAVAILABLE_REASON.unavailable, assumptions: [] };
}

// ─── КБЖУ товара на выбранном количестве ─────────────────────────────────

export interface ProductNutritionSource {
  basisUnit: "g" | "ml";
  per100: NutrientValues;
  /** Только явное поле packNet каталога; текст «500 г» не разбирается. */
  packNet: { amount: number; unit: "g" | "ml" } | null;
}

const finiteNonNegative = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;

/** Текущее поле product.nutrition (на 100 г/мл) → нормализованный источник. Нет данных → null. */
export function productNutritionSource(product: { nutrition?: unknown; packNet?: unknown } | null | undefined): ProductNutritionSource | null {
  const n = product?.nutrition as { kcal?: unknown; protein?: unknown; fat?: unknown; carbs?: unknown; basis?: unknown } | undefined;
  if (!n || (n.basis !== "100g" && n.basis !== "100ml")) return null;
  const basisUnit = n.basis === "100ml" ? "ml" : "g";
  const val = (v: unknown) => (finiteNonNegative(v) ? v : null);
  const per100: NutrientValues = { kcal: val(n.kcal), protein_g: val(n.protein), fat_g: val(n.fat), carbs_g: val(n.carbs) };
  const raw = (product as { packNet?: unknown }).packNet as { amount?: unknown; unit?: unknown } | null | undefined;
  const packNet = raw && finiteNonNegative(raw.amount) && raw.amount > 0 && raw.unit === basisUnit
    ? { amount: raw.amount, unit: basisUnit } as const : null;
  return { basisUnit, per100, packNet };
}

/** Все четыре значения на выбранной основе. г и мл не конвертируются; упаковка — только по явной массе нетто. */
export function productNutritionView(src: ProductNutritionSource | null, basis: NutritionBasis): NutritionView {
  const unavailable = (reason: string): NutritionView => ({ basis, status: "unavailable", values: NULL_VALUES(), reason, assumptions: [] });
  if (!src) return unavailable("Производитель не передал КБЖУ этого товара.");
  const unitWord = src.basisUnit === "ml" ? "в миллилитрах" : "в граммах";
  let amount: number;
  if (basis.kind === "per100g" || basis.kind === "per100ml") {
    if ((basis.kind === "per100ml") !== (src.basisUnit === "ml")) return unavailable(`КБЖУ этого товара указано ${unitWord}.`);
    amount = 100;
  } else if (basis.kind === "pack") {
    if (!src.packNet) return unavailable("Масса нетто упаковки не указана, поэтому КБЖУ на упаковку не считаем.");
    amount = src.packNet.amount;
  } else if (basis.kind === "custom") {
    if (validateBasis(basis)) return unavailable("Введите количество больше нуля.");
    if (basis.unit !== src.basisUnit) return unavailable(`КБЖУ этого товара указано ${unitWord}.`);
    amount = basis.amount;
  } else {
    return unavailable("Для товара нет КБЖУ на порцию рецепта.");
  }
  const values = NULL_VALUES();
  for (const k of NUTRIENTS) {
    const v = src.per100[k];
    values[k] = v === null ? null : (v * amount) / 100;
  }
  const known = NUTRIENTS.filter((k) => values[k] !== null).length;
  if (known === 0) return unavailable("Производитель не указал значения КБЖУ.");
  return {
    basis, values, assumptions: [],
    status: known === NUTRIENTS.length ? "complete" : "partial",
    reason: known === NUTRIENTS.length ? null : "Производитель указал не все показатели.",
  };
}
